import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, X, RotateCcw, Check, AlertTriangle } from "lucide-react";
import logger from "@/lib/logger";

function CameraView({ videoRef, onCapture, onSwitchCamera, hasFrontCamera }) {
  return (
    <div className="space-y-3">
      <div className="relative bg-black rounded-lg overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-64 object-cover" />
      </div>
      <div className="flex justify-center gap-3">
        {hasFrontCamera && (
          <Button variant="outline" size="sm" onClick={onSwitchCamera} className="h-10 w-10 rounded-full p-0" data-testid="switch-camera-btn">
            <RotateCcw size={16} />
          </Button>
        )}
        <Button onClick={onCapture} className="h-14 w-14 rounded-full bg-[#E31E24] hover:bg-red-700 p-0" data-testid="capture-photo-btn">
          <Camera size={24} />
        </Button>
      </div>
    </div>
  );
}

function PhotoPreview({ previewSrc, onAccept, onRetake }) {
  return (
    <div className="space-y-3">
      <div className="relative bg-black rounded-lg overflow-hidden">
        <img src={previewSrc} alt="Preview" className="w-full h-64 object-cover" />
      </div>
      <div className="flex justify-center gap-3">
        <Button variant="outline" onClick={onRetake} className="h-11 gap-2" data-testid="retake-photo-btn">
          <RotateCcw size={16} /> Retake
        </Button>
        <Button onClick={onAccept} className="h-11 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="accept-photo-btn">
          <Check size={16} /> Use Photo
        </Button>
      </div>
    </div>
  );
}

function NoCameraFallback() {
  return (
    <div className="flex flex-col items-center justify-center h-48 text-gray-400">
      <AlertTriangle size={32} className="mb-2" />
      <p className="text-sm">Camera not available on this device</p>
      <p className="text-xs mt-1">Please use a device with camera access</p>
    </div>
  );
}

export default function CameraCapture({ open, onClose, onCapture: onCaptureCallback, existingPhoto }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [hasFrontCamera, setHasFrontCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
  }, [stream]);

  const startCamera = useCallback(async (mode) => {
    stopStream();
    setCameraError(false);
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      setCameraReady(true);
    } catch (err) {
      logger.error("Camera access error:", err);
      setCameraError(true);
      setCameraReady(false);
    }
  }, [stopStream]);

  useEffect(() => {
    if (open && !preview) {
      startCamera(facingMode);
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const cameras = devices.filter(d => d.kind === "videoinput");
        setHasFrontCamera(cameras.length > 1);
      }).catch(() => {});
    }
    return () => { if (!open) stopStream(); };
  }, [open, facingMode, preview, startCamera, stopStream]);

  const handleCapture = () => {
    if (!videoRef.current) return;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvasRef.current = canvas;
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    setPreview(dataUrl);
    stopStream();
  };

  const handleAccept = () => {
    if (preview && onCaptureCallback) {
      onCaptureCallback(preview);
    }
    setPreview(null);
    stopStream();
    onClose();
  };

  const handleRetake = () => {
    setPreview(null);
    startCamera(facingMode);
  };

  const handleSwitchCamera = () => {
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
  };

  const handleClose = () => {
    setPreview(null);
    stopStream();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="camera-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              <Camera size={18} className="inline mr-2" />
              {preview ? "Photo Preview" : "Capture Photo"}
            </DialogTitle>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleClose}>
              <X size={16} />
            </Button>
          </div>
        </DialogHeader>

        {existingPhoto && !preview && !cameraReady && (
          <div className="mb-2">
            <p className="text-xs text-gray-500 mb-1">Current photo:</p>
            <img src={existingPhoto} alt="Current" className="w-20 h-20 rounded border object-cover" />
          </div>
        )}

        {cameraError ? (
          <NoCameraFallback />
        ) : preview ? (
          <PhotoPreview previewSrc={preview} onAccept={handleAccept} onRetake={handleRetake} />
        ) : (
          <CameraView
            videoRef={videoRef}
            onCapture={handleCapture}
            onSwitchCamera={handleSwitchCamera}
            hasFrontCamera={hasFrontCamera}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
